import React from 'react';
import { motion } from 'framer-motion';
import { X, Globe, MapPin, TrendingUp, Users } from 'lucide-react';

const CountryDetailsModal = ({ isOpen, onClose, stats, onCountryClick }) => {
  if (!isOpen) return null;

  const getFlagEmoji = (countryCode) => {
    if (!countryCode || countryCode === 'XX' || countryCode === 'LOCAL') return '🌍';
    
    const flagMap = {
      'FR': '🇫🇷', 'US': '🇺🇸', 'GB': '🇬🇧', 'DE': '🇩🇪', 'CA': '🇨🇦',
      'AU': '🇦🇺', 'JP': '🇯🇵', 'IT': '🇮🇹', 'ES': '🇪🇸', 'NL': '🇳🇱',
      'CH': '🇨🇭', 'BE': '🇧🇪', 'SE': '🇸🇪', 'NO': '🇳🇴', 'DK': '🇩🇰',
      'FI': '🇫🇮', 'PT': '🇵🇹', 'BR': '🇧🇷', 'MX': '🇲🇽', 'AR': '🇦🇷',
      'IN': '🇮🇳', 'CN': '🇨🇳', 'KR': '🇰🇷', 'SG': '🇸🇬', 'MY': '🇲🇾',
      'TH': '🇹🇭', 'PH': '🇵🇭', 'VN': '🇻🇳', 'ID': '🇮🇩', 'LOCAL': '🏠'
    };
    
    return flagMap[countryCode] || '🌍';
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-slate-900 rounded-3xl border border-white/10 shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 relative">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Détails par Pays</h2>
                <p className="text-blue-100 text-sm">Distribution géographique de vos visiteurs</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats?.views_by_country?.length || 0}</div>
                  <div className="text-gray-300 text-sm">Pays uniques</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{stats?.top_cities?.length || 0}</div>
                  <div className="text-gray-300 text-sm">Villes uniques</div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-4 border border-white/10">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">
                    {stats?.views_by_country?.[0]?.percentage || 0}%
                  </div>
                  <div className="text-gray-300 text-sm">Top pays</div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
            {/* Countries List */}
            <div>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <Globe className="w-5 h-5 mr-2" />
                Vues par Pays
              </h3>
              
              {stats?.views_by_country?.length > 0 ? (
                <div className="space-y-3">
                  {stats.views_by_country.map((country, index) => (
                    <motion.div
                      key={country.country_code}
                      className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors cursor-pointer"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      onClick={() => onCountryClick && onCountryClick(country.country_code)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="text-2xl">{getFlagEmoji(country.country_code)}</div>
                          <div>
                            <div className="text-white font-medium">{country.country}</div>
                            <div className="text-gray-300 text-sm">Cliquez pour voir les détails</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">{country.count}</div>
                          <div className="text-gray-400 text-sm">vues</div>
                        </div>
                      </div>
                      
                      {/* Progress bar */}
                      <div className="mt-3 bg-white/10 rounded-full h-2 overflow-hidden">
                        <motion.div
                          className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                          initial={{ width: 0 }}
                          animate={{ width: `${country.percentage}%` }}
                          transition={{ duration: 1, delay: index * 0.1 + 0.5 }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune donnée géographique disponible</p>
                  <p className="text-sm mt-1">Les données apparaîtront après les premières visites</p>
                </div>
              )}
            </div>

            {/* Top Cities */}
            <div>
              <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                <MapPin className="w-5 h-5 mr-2" />
                Top Villes
              </h3>
              
              {stats?.top_cities?.length > 0 ? (
                <div className="space-y-3">
                  {stats.top_cities.map((city, index) => (
                    <motion.div
                      key={`${city.city}-${city.country}`}
                      className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 hover:bg-white/10 transition-colors"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                            #{index + 1}
                          </div>
                          <div>
                            <div className="text-white font-medium">{city.city}</div>
                            <div className="text-gray-400 text-sm">{city.country}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-white">{city.count}</div>
                          <div className="text-gray-400 text-sm">vues</div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune donnée de ville disponible</p>
                  <p className="text-sm mt-1">Les données apparaîtront après les premières visites</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CountryDetailsModal;